/**
 * The review of a version's inline Markdown (DESIGN UD-31, ED-152 to ED-154): the types shared by the analysis worker (which
 * makes the review and applies the edits) and the page (which shows and stores them), and the pure helpers of the editor: the
 * Bold and Italic toggles on a selection, and a small inline reader that gives the preview and the plain text an edit is
 * compared by. No DOM, no engine.
 */

/** One paragraph's Markdown as the operator set it: it replaces the automatic Markdown of that paragraph in every download. */
export interface MarkdownEdit {
	/** The paragraph's block in the original document. */
	block: number;
	/** `blockBase` of the paragraph's text in the version: the edit applies only while that text is the same. */
	base: string;
	/** The paragraph's inline Markdown (no heading, list or quote marker; lines joined by `\n`). */
	md: string;
}

/** What became of one inline span of a rewritten paragraph (the same words as the export's notice). */
export interface ReviewSpan {
	status: 'carried' | 'moved' | 'dropped';
	/** The span as it was written in the original. */
	before: string;
	/** The span as written in the new text (absent when dropped). */
	after?: string;
}

/** One rewritten paragraph in the review. */
export interface ReviewItem {
	block: number;
	/** The paragraph's number (1-based) in the original. */
	para: number;
	/** The paragraph's new text, plain. */
	text: string;
	/** `blockBase(text)`: what an edit is stored against. */
	base: string;
	/** The original paragraph's Markdown (no block marker). */
	original: string;
	/** The automatic Markdown of the new paragraph. */
	auto: string;
	/** The operator's edit when it applies to this text. */
	edit?: string;
	/** An edit made against other text (the version was revised since): not applied, shown for the operator to reuse. */
	stale?: string;
	spans: ReviewSpan[];
}

/** Whitespace folded: how texts are compared. */
export const foldText = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** FNV-1a over the whitespace-folded text, as 8 hex digits: what an edit is stored against. */
export function blockBase(text: string): string {
	const s = foldText(text);
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return (h >>> 0).toString(16).padStart(8, '0');
}

/** The edit that applies to `block` whose text in the version is `text`, or undefined. */
export function editFor(
	edits: readonly MarkdownEdit[] | undefined,
	block: number,
	text: string
): MarkdownEdit | undefined {
	if (!edits || edits.length === 0) return undefined;
	const base = blockBase(text);
	return edits.find((e) => e.block === block && e.base === base);
}

/** Edits read back from storage or a message: only well-formed ones, at most one per block (the last). */
export function cleanEdits(raw: unknown): MarkdownEdit[] {
	if (!Array.isArray(raw)) return [];
	const by = new Map<number, MarkdownEdit>();
	for (const e of raw) {
		if (!e || typeof e !== 'object') continue;
		const { block, base, md } = e as Record<string, unknown>;
		if (!Number.isInteger(block) || (block as number) < 0) continue;
		if (typeof base !== 'string' || typeof md !== 'string') continue;
		by.set(block as number, { block: block as number, base, md });
	}
	return [...by.values()].sort((a, b) => a.block - b.block);
}

/* ---------------- the inline reader ---------------- */

export type InlineNode =
	| { t: 'text'; s: string }
	| { t: 'code'; s: string }
	| { t: 'em' | 'strong' | 'del' | 'link'; kids: InlineNode[] };

const PUNCT = /[!-/:-@[-`{-~]/;
const isSpace = (c: string | undefined): boolean => c === undefined || /\s/.test(c);
const isWord = (c: string | undefined): boolean => c !== undefined && /[\p{L}\p{N}]/u.test(c);

/** The length of the run of `c` starting at `i`. */
function run(s: string, i: number, c: string): number {
	let n = 0;
	while (s[i + n] === c) n++;
	return n;
}

/** Whether the run of `c` (length `n`) at `i` may open emphasis, and may close it. */
function flanks(s: string, i: number, n: number, c: string): { open: boolean; close: boolean } {
	const before = s[i - 1];
	const after = s[i + n];
	let open = !isSpace(after);
	let close = !isSpace(before);
	if (c === '_') {
		open = open && !isWord(before);
		close = close && !isWord(after);
	}
	return { open, close };
}

/** The end of the code span that opens with `n` backticks at `i` (the index of its closing run), or -1. */
function codeEnd(s: string, i: number, n: number): number {
	let j = i + n;
	while (j < s.length) {
		if (s[j] !== '`') {
			j++;
			continue;
		}
		const m = run(s, j, '`');
		if (m === n) return j;
		j += m;
	}
	return -1;
}

/** The closing run (index) for an opener of `k` delimiters `c` whose content starts at `from`, or -1. */
function findCloser(s: string, from: number, c: string, k: number): number {
	let j = from;
	while (j < s.length) {
		const ch = s[j];
		if (ch === '\\') {
			j += 2;
			continue;
		}
		if (ch === '`') {
			const n = run(s, j, '`');
			const e = codeEnd(s, j, n);
			j = e < 0 ? j + n : e + n;
			continue;
		}
		if (ch !== c) {
			j++;
			continue;
		}
		const n = run(s, j, c);
		const f = flanks(s, j, n, c);
		if (n === k && f.close) return j;
		j += n;
	}
	return -1;
}

/** Reads inline Markdown into nodes: emphasis, strong, strike, code, links and backslash escapes. Unmatched markers are text. */
export function parseInline(s: string): InlineNode[] {
	const out: InlineNode[] = [];
	let text = '';
	const flush = () => {
		if (text !== '') out.push({ t: 'text', s: text });
		text = '';
	};
	let i = 0;
	while (i < s.length) {
		const c = s[i] as string;
		if (c === '\\' && i + 1 < s.length && PUNCT.test(s[i + 1] as string)) {
			text += s[i + 1];
			i += 2;
			continue;
		}
		if (c === '`') {
			const n = run(s, i, '`');
			const e = codeEnd(s, i, n);
			if (e < 0) {
				text += '`'.repeat(n);
				i += n;
				continue;
			}
			let body = s.slice(i + n, e);
			if (body.length > 1 && body.startsWith(' ') && body.endsWith(' ') && body.trim() !== '')
				body = body.slice(1, -1);
			flush();
			out.push({ t: 'code', s: body });
			i = e + n;
			continue;
		}
		if (c === '<') {
			const m = /^<([a-zA-Z][a-zA-Z0-9+.-]*:[^\s<>]*)>/.exec(s.slice(i, i + 2000));
			if (m) {
				flush();
				out.push({ t: 'link', kids: [{ t: 'text', s: m[1] as string }] });
				i += m[0].length;
				continue;
			}
		}
		if (c === '[') {
			const close = findBracket(s, i);
			if (close > 0) {
				const tail =
					/^(?:\([^)\n]*\)|\[[^\]\n]*\])?/.exec(s.slice(close + 1, close + 2000))?.[0] ?? '';
				flush();
				out.push({ t: 'link', kids: parseInline(s.slice(i + 1, close)) });
				i = close + 1 + tail.length;
				continue;
			}
		}
		if (c === '*' || c === '_' || c === '~') {
			const n = run(s, i, c);
			if (c === '~' && n < 2) {
				text += c;
				i += n;
				continue;
			}
			const f = flanks(s, i, n, c);
			if (f.open) {
				const k = c === '~' ? 2 : Math.min(n, 3);
				const e = findCloser(s, i + n, c, k);
				if (e >= 0) {
					const lead = n - k;
					text += c.repeat(lead);
					flush();
					const inner = parseInline(s.slice(i + n, e));
					let node: InlineNode;
					if (c === '~') node = { t: 'del', kids: inner };
					else if (k === 1) node = { t: 'em', kids: inner };
					else if (k === 2) node = { t: 'strong', kids: inner };
					else node = { t: 'em', kids: [{ t: 'strong', kids: inner }] };
					out.push(node);
					i = e + k;
					continue;
				}
			}
			text += c.repeat(n);
			i += n;
			continue;
		}
		text += c;
		i++;
	}
	flush();
	return out;
}

/** The `]` that closes the `[` at `i`, or -1. */
function findBracket(s: string, i: number): number {
	let depth = 0;
	for (let j = i; j < s.length; j++) {
		const c = s[j];
		if (c === '\\') {
			j++;
			continue;
		}
		if (c === '`') {
			const n = run(s, j, '`');
			const e = codeEnd(s, j, n);
			j = e < 0 ? j + n - 1 : e + n - 1;
			continue;
		}
		if (c === '[') depth++;
		else if (c === ']' && --depth === 0) return j;
	}
	return -1;
}

/** The words of inline Markdown without its markup: what the paragraph says. */
export function plainOf(md: string): string {
	const flat = (nodes: InlineNode[]): string =>
		nodes.map((n) => (n.t === 'text' || n.t === 'code' ? n.s : flat(n.kids))).join('');
	return foldText(flat(parseInline(md)));
}

/* ---------------- the toggles ---------------- */

export type Mark = 'bold' | 'italic';

/** A selection in a text: `start` to `end`, `end` exclusive. */
export interface Selection {
	start: number;
	end: number;
}

const WORD = /[\p{L}\p{N}'’-]/u;

/** The selection without whitespace at its ends; an empty one grows to the word around it. */
function tighten(md: string, sel: Selection): Selection {
	let { start, end } = sel;
	if (start === end) {
		while (start > 0 && WORD.test(md[start - 1] as string)) start--;
		while (end < md.length && WORD.test(md[end] as string)) end++;
	}
	while (start < end && /\s/.test(md[start] as string)) start++;
	while (end > start && /\s/.test(md[end - 1] as string)) end--;
	return { start, end };
}

/** How many of `c` end just before `at`. */
function around(md: string, at: number, c: string): number {
	let n = 0;
	while (n < at && md[at - 1 - n] === c) n++;
	return n;
}

const present = (n: number, mark: Mark): boolean =>
	mark === 'italic' ? n === 1 || n === 3 : n === 2 || n === 3;

/**
 * Bold or Italic on the selection: wraps it (`**` or `*`), or unwraps it when the markers are already there, just outside it
 * or inside its ends (`*` or `_`, `**` or `__`, and the three together). Whitespace at the selection's ends is left out of
 * the markers, and an empty selection means the word it is in. Returns the new text and the selection of the same words;
 * unchanged when there is nothing to wrap.
 */
export function toggleMark(
	md: string,
	sel: Selection,
	mark: Mark
): { md: string; selection: Selection } {
	const { start, end } = tighten(md, sel);
	if (start >= end) return { md, selection: sel };
	const len = mark === 'bold' ? 2 : 1;
	for (const c of ['*', '_']) {
		// markers just outside the selection
		const before = around(md, start, c);
		let after = 0;
		while (md[end + after] === c) after++;
		const outside = Math.min(before, after);
		if (outside > 0 && outside <= 3 && present(outside, mark)) {
			return {
				md: md.slice(0, start - len) + md.slice(start, end) + md.slice(end + len),
				selection: { start: start - len, end: end - len },
			};
		}
		// markers inside the selection's ends
		const sub = md.slice(start, end);
		let lead = 0;
		let trail = 0;
		while (sub[lead] === c) lead++;
		while (sub[sub.length - 1 - trail] === c) trail++;
		const inside = Math.min(lead, trail, 3);
		if (inside > 0 && lead + trail < sub.length && present(inside, mark)) {
			const cut = sub.slice(len, sub.length - len);
			return {
				md: md.slice(0, start) + cut + md.slice(end),
				selection: { start, end: start + cut.length },
			};
		}
	}
	const m = mark === 'bold' ? '**' : '*';
	return {
		md: `${md.slice(0, start)}${m}${md.slice(start, end)}${m}${md.slice(end)}`,
		selection: { start: start + m.length, end: end + m.length },
	};
}
